import { Disclosure } from "@headlessui/react";
import { ExpandLess, ExpandMore, InfoOutlined, RefreshRounded } from "@mui/icons-material";
import { Card, Tooltip, Typography } from "@mui/material";
import AnimatedNumber from "animated-number-react";
import { useEffect, useState } from "react";
import CountryFlag from "react-country-flag";
import { useDebounce } from "use-debounce";
import { Button } from "~/components/ui/core/Button";
import { Icon } from "~/components/ui/core/Icon";
import { LegacyHeading } from "~/components/ui/core/LegacyHeading";
import { LegacyText } from "~/components/ui/core/LegacyText";
import { type ExternalLink, Link } from "~/components/ui/core/Link";
import { Ping } from "~/components/ui/core/Ping";
import { LocationSelector } from "~/components/ui/LocationSelector";
import { PercentageDifference } from "~/components/ui/PercentageDifference";
import { useApi } from "~/hooks/useApi";
import { useForm } from "~/hooks/useForm";
import { PlaceholderCurrency, useMoney } from "~/hooks/useMoney";
import { useListCurrenciesQuery, useListLocationsQuery } from "~/hooks/useQueries";
import { trackSalaryConverterSearched } from "~/lib/external/segment/web/events";
import { formatPercent } from "~/lib/math";
import { convertCurrency } from "~/lib/money";
import { type GetLandingStatsResponse } from "~/pages/api/get-landing-stats";
import { type EmployeeLocationWithCountry } from "~/services/employee/employeeLocation";
import { type ConvertSalaryResponse } from "~/services/salaryConversion";

type FormValues = {
  amount: number;
  fromLocation: EmployeeLocationWithCountry | null;
  toLocation: EmployeeLocationWithCountry | null;
};

type Props = {
  initialValues?: FormValues;
  initialData?: ConvertSalaryResponse;
  initialLandingStats?: GetLandingStatsResponse;
  onChange?: (formValues: FormValues) => void;
};

const defaultValues = {
  to: "Berlin",
  from: "Paris",
  amount: 50_000,
};

export const GlobalSalaryConverter: React.FC<Props> = ({
  initialValues: propsInitialValues,
  initialData,
  initialLandingStats,
  onChange,
}) => {
  const { apiFetch } = useApi();
  const [useConvertedCurrency, setUseConvertedCurrency] = useState(false);
  const [showEmploymentCostSection, setShowEmploymentCostSection] = useState(false);
  const [data, setData] = useState<ConvertSalaryResponse | undefined>(initialData);
  const [landingStats, setLandingStats] = useState<GetLandingStatsResponse | undefined>(initialLandingStats);

  const { data: locationData } = useListLocationsQuery({ hideRemote: true, hideOther: true });
  const { data: currenciesData } = useListCurrenciesQuery();

  const targetData = useConvertedCurrency ? data?.converted : data?.local;

  const localMoney = useMoney(data?.local.currency ?? PlaceholderCurrency);
  const money = useMoney(targetData?.currency ?? PlaceholderCurrency);

  const form = useForm<FormValues>({
    initialValues: propsInitialValues || { amount: defaultValues.amount, toLocation: null, fromLocation: null },
    onSubmit: () => {
      // Handled below when any input changes
    },
  });
  const [debouncedAmount] = useDebounce(form.values.amount, 300);

  useEffect(() => {
    if (!!form.values.fromLocation && !!form.values.toLocation && !!form.values.amount) {
      return;
    }

    const toLocation = locationData?.locations.find((location) => location.name === defaultValues.to) ?? null;
    const fromLocation = locationData?.locations.find((location) => location.name === defaultValues.from) ?? null;
    const amount = defaultValues.amount;

    void form.setValues({ toLocation, fromLocation, amount });
    return;
  }, [locationData]);

  useEffect(() => {
    const fetch = async () => {
      if (!debouncedAmount || !form.values.fromLocation || !form.values.toLocation) {
        return;
      }

      const data = await apiFetch<ConvertSalaryResponse>("/api/convert-salary", {
        body: {
          toLocationId: form.values.toLocation.id,
          fromLocationId: form.values.fromLocation.id,
          amount: debouncedAmount,
        },
      });

      setData(data);

      await trackSalaryConverterSearched({
        amount: debouncedAmount,
        fromLocation: form.values.fromLocation,
        toLocation: form.values.toLocation,
      });

      onChange?.({
        amount: debouncedAmount,
        fromLocation: form.values.fromLocation,
        toLocation: form.values.toLocation,
      });
    };

    void fetch();
  }, [debouncedAmount, form.values.fromLocation, form.values.toLocation]);

  useEffect(() => {
    if (!!landingStats) {
      return;
    }

    const fetch = async () => {
      const data = await apiFetch<GetLandingStatsResponse>("/api/get-landing-stats");
      setLandingStats(data);
    };

    void fetch();
  }, []);

  if (!data || !targetData) {
    return null;
  }

  const targetDataWithEmploymentCost = data.destination.employmentCost
    ? {
        currency: targetData?.currency,
        amounts: {
          costOfLiving: targetData.amounts.costOfLiving
            ? targetData.amounts.costOfLiving * (1 + (data.destination.employmentCost ?? 0))
            : null,
          costOfLabor: targetData.amounts.costOfLabor
            ? targetData.amounts.costOfLabor * (1 + (data.destination.employmentCost ?? 0))
            : null,
        },
      }
    : targetData;
  const sourceAmountWithEmploymentCost = form.values.amount * (1 + (data?.source.employmentCost ?? 0));

  const convertedSourceAmountWithEmploymentCost = convertCurrency(
    sourceAmountWithEmploymentCost,
    data.local.currency,
    targetData.currency
  );

  const guessedBasedCurrency = (currenciesData?.currencies ?? []).find((currency) => {
    return form.values.fromLocation?.country.defaultCurrencyId === currency.id;
  });

  const showMoreInformationSection =
    !!data.destination.oysterUrl || !!data.destination.nomadListUrl || !!data.destination.youTubeSongUrl;

  return (
    <div>
      <form className="flex flex-col" onSubmit={form.handleSubmit}>
        <div className="relative animate-scale rounded">
          <Card className="!p-5 pt-3">
            <div className="-m-5 mb-0 rounded-t bg-primary-200 p-4">
              <LegacyHeading className="font-title normal-case" size="4xl">
                Global Salary Converter
              </LegacyHeading>
            </div>

            <div className="mt-4 ml-1 flex flex-wrap space-x-3 font-title text-xl !leading-relaxed sm:text-3xl">
              <span>How much should an employee based in</span>
              <LocationSelector
                hideOther
                hideRemote
                formatLabel={([location]) => {
                  return (
                    <span className="border-b-2 border-primary-300 pb-1 sm:pb-2">
                      {location && (
                        <CountryFlag svg className="mr-2 -mt-1" countryCode={location.country.alpha2 as string} />
                      )}
                      <span className="font-bold">{location?.name}</span>
                    </span>
                  );
                }}
                orderBy="name"
                value={form.values.fromLocation ? [form.values.fromLocation] : []}
                onChange={(event) => {
                  const location = event.target.value[0];

                  if (location) {
                    void form.setFieldValue("fromLocation", location);
                  }
                }}
              />
              <span>with a base salary of </span>
              <span className="h-12 border-b-2 border-primary-300">
                <input
                  className="mr-1 w-24 bg-transparent pr-1 text-right font-bold focus:outline-none sm:w-32"
                  type="number"
                  value={form.values.amount}
                  onChange={(event) => {
                    void form.setFieldValue("amount", parseInt(event.target.value));
                  }}
                />
                <span>{guessedBasedCurrency?.symbol}</span>
              </span>
              <span>earn in</span>
              <LocationSelector
                hideOther
                hideRemote
                formatLabel={([location]) => {
                  return (
                    <span className="border-b-2 border-primary-300 pb-1 sm:pb-2">
                      {location && (
                        <CountryFlag svg className="mr-2 -mt-1" countryCode={location.country.alpha2 as string} />
                      )}
                      <span className="font-bold">{location?.name}</span>
                    </span>
                  );
                }}
                orderBy="name"
                value={form.values.toLocation ? [form.values.toLocation] : []}
                onChange={(event) => {
                  const location = event.target.value[0];

                  if (location) {
                    void form.setFieldValue("toLocation", location);
                  }
                }}
              />
              ?
            </div>

            {data && data.warnings.length > 0 && (
              <Card className="mt-8 flex items-center border-l-4 border-secondary-400 bg-gradient-to-br from-secondary-50 to-secondary-100">
                <Ping color="secondary" />
                <span className="ml-2 flex flex-col text-sm text-gray-800">
                  <span>
                    Warning: low confidence for the selected locations. The displayed data might not be reliable.
                  </span>
                  {data.warnings.map((warning) => {
                    return (
                      <span key={warning} className="ml-2">
                        - {warning}
                      </span>
                    );
                  })}
                </span>
              </Card>
            )}

            <div className="mt-12 grid grid-cols-1 gap-y-8 gap-x-8 lg:grid-cols-3 lg:divide-x">
              {/* Salary from source location */}
              <div>
                <span className="mb-2 flex items-center border-b pb-2 text-xl  font-semibold uppercase text-gray-500">
                  {form.values.fromLocation && (
                    <CountryFlag svg countryCode={form.values.fromLocation.country.alpha2} />
                  )}
                  <span className="ml-2 truncate text-clip whitespace-nowrap">
                    Gross salary in {form.values.fromLocation?.name}
                  </span>
                </span>

                <div className="mt-4 flex items-end lg:mt-11">
                  <span className="text-4xl font-semibold text-gray-600 ">
                    <AnimatedNumber
                      duration={150}
                      formatValue={(value) => {
                        return localMoney.format(value, { roundTo: 1 });
                      }}
                      value={form.values.amount * 100}
                    />
                  </span>
                </div>
              </div>

              {/* Converted salary for target location */}
              <div className="col-span-2 flex flex-1 flex-col lg:pl-8">
                <span className="mb-2 flex items-center border-b pb-2 text-xl  font-semibold uppercase text-gray-500">
                  {form.values.toLocation && <CountryFlag svg countryCode={form.values.toLocation.country.alpha2} />}
                  <span className="ml-2 whitespace-nowrap">Gross salary in {form.values.toLocation?.name}</span>
                </span>

                <div className="flex flex-1 flex-col justify-between space-y-4 lg:flex-row lg:space-y-0">
                  {/* Cost of labor */}
                  <div>
                    <span className="flex items-center space-x-1 font-medium uppercase text-gray-500">
                      <span className="whitespace-nowrap">Based on cost of labor</span>

                      <Tooltip
                        title={
                          <Typography variant="inherit">
                            <span className="font-semibold">“Cost of labor”</span> refers to the difference in pay or
                            labor market for a job from one location to another. Another way to phrase this is that the
                            cost of labor is what a particular geographic market offers as the “going rate” or
                            compensation for its jobs.
                          </Typography>
                        }
                      >
                        <InfoOutlined fontSize="small" className="ml-2 text-gray-400" />
                      </Tooltip>
                    </span>

                    <div className="mt-3 flex items-end">
                      {data && targetData?.amounts.costOfLabor ? (
                        <>
                          <span className="text-4xl font-semibold text-primary-600 ">
                            <AnimatedNumber
                              duration={150}
                              formatValue={(value) => {
                                return money.format(value, { roundTo: 100 });
                              }}
                              value={targetData.amounts.costOfLabor}
                            />
                          </span>
                          <span className="ml-auto text-xl font-semibold text-primary-600 lg:ml-2">
                            <span className="font-light text-primary-200">(</span>
                            <PercentageDifference
                              compare={data.local.amounts.costOfLabor}
                              to={form.values.amount * 100}
                            />
                            <span className="font-light text-primary-200">)</span>
                          </span>
                        </>
                      ) : (
                        <span className="text-5xl font-semibold text-primary-600">-</span>
                      )}
                    </div>
                  </div>

                  {/* Cost of living */}
                  <div>
                    <span className="flex items-center space-x-1 font-medium uppercase text-gray-500">
                      <span className="whitespace-nowrap">Based on cost of living</span>

                      <Tooltip
                        title={
                          <Typography variant="inherit">
                            <span className="font-semibold">“Cost of living”</span> refers to the costs to a consumer in
                            a specific geographic area. It reflects the price of food, housing, groceries,
                            transportation, taxes and entertainment. Simply put, it’s the cost of maintaining a certain
                            standard of living.
                          </Typography>
                        }
                      >
                        <InfoOutlined fontSize="small" className="ml-2 text-gray-400" />
                      </Tooltip>
                    </span>

                    <div className="mt-3 flex items-end">
                      {data && targetData?.amounts.costOfLiving ? (
                        <>
                          <span className="text-4xl font-semibold text-primary-600 ">
                            <AnimatedNumber
                              duration={150}
                              formatValue={(value) => {
                                return money.format(value, { roundTo: 100 });
                              }}
                              value={targetData.amounts.costOfLiving}
                            />
                          </span>
                          <span className="ml-auto text-xl font-semibold text-primary-600 lg:ml-2">
                            <span className="font-light text-primary-200">(</span>
                            <PercentageDifference
                              compare={data.local.amounts.costOfLiving}
                              to={form.values.amount * 100}
                            />
                            <span className="font-light text-primary-200">)</span>
                          </span>
                        </>
                      ) : (
                        <span className="text-5xl font-semibold text-primary-600">-</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {data?.destination.employmentCost && (
              <Disclosure as="div" className="mt-6 flex flex-col border-t pt-4" defaultOpen={showEmploymentCostSection}>
                <Disclosure.Button as="div">
                  {({ open }) => {
                    return (
                      <div className="flex items-center">
                        <Button
                          color="inherit"
                          size="small"
                          variant="outlined"
                          startIcon={open ? <ExpandLess /> : <ExpandMore />}
                          onClick={() => setShowEmploymentCostSection(!showEmploymentCostSection)}
                        >
                          {open ? "Hide employment costs" : "Show employment costs"}
                        </Button>

                        <Tooltip
                          title={
                            <Typography variant="inherit">
                              <span className="font-semibold">“The employment cost“</span> refers to the total amount of
                              money that it costs a company to employ people, including pay, insurance, benefits etc.
                            </Typography>
                          }
                        >
                          <InfoOutlined fontSize="small" className="ml-2 text-gray-400" />
                        </Tooltip>
                      </div>
                    );
                  }}
                </Disclosure.Button>
                <Disclosure.Panel>
                  <div className="mt-8 grid grid-cols-1 gap-y-8 gap-x-8 lg:grid-cols-3 lg:divide-x">
                    {/* Salary from source location */}
                    <div>
                      <span className="flex items-center text-xl font-semibold uppercase text-gray-500 ">
                        {form.values.fromLocation && (
                          <CountryFlag svg countryCode={form.values.fromLocation.country.alpha2} />
                        )}
                        <span className="ml-2 truncate text-clip whitespace-nowrap">
                          Employer cost in {form.values.fromLocation?.name}
                        </span>
                      </span>

                      <span className="mb-2 flex items-center border-b pb-2 pl-7  text-sm font-medium uppercase text-gray-500">
                        (Est. employment cost of {formatPercent(data.source.employmentCost ?? 0)})
                      </span>

                      <div className="mt-4 flex items-end lg:mt-11">
                        <span className="text-4xl font-semibold text-gray-600 ">
                          <AnimatedNumber
                            duration={150}
                            formatValue={(value) => {
                              return localMoney.format(value, { roundTo: 100 });
                            }}
                            value={sourceAmountWithEmploymentCost * 100}
                          />
                        </span>
                      </div>
                    </div>

                    {/* Converted salary for target location */}
                    <div className="col-span-2 flex flex-1 flex-col lg:pl-8">
                      <span className="flex items-center text-xl font-semibold uppercase text-gray-500 ">
                        {form.values.toLocation && (
                          <CountryFlag svg countryCode={form.values.toLocation.country.alpha2} />
                        )}
                        <span className="ml-2 whitespace-nowrap">Employer cost in {form.values.toLocation?.name}</span>
                      </span>

                      <span className="mb-2 flex items-center border-b pb-2 pl-7  text-sm font-medium uppercase text-gray-500">
                        (Est. employment cost of {formatPercent(data.destination.employmentCost ?? 0)})
                      </span>

                      <div className="flex flex-1 flex-col justify-between space-y-4 lg:flex-row lg:space-y-0">
                        {/* Cost of labor */}
                        <div>
                          <span className="flex items-center space-x-1 font-medium uppercase text-gray-500">
                            <span className="whitespace-nowrap">Based on cost of labor</span>

                            <Tooltip
                              title={
                                <Typography variant="inherit">
                                  <span className="font-semibold">“Cost of labor”</span> refers to the difference in pay
                                  or labor market for a job from one location to another. Another way to phrase this is
                                  that the cost of labor is what a particular geographic market offers as the “going
                                  rate” or compensation for its jobs.
                                </Typography>
                              }
                            >
                              <InfoOutlined fontSize="small" className="ml-2 text-gray-400" />
                            </Tooltip>
                          </span>

                          <div className="mt-3 flex items-end">
                            {data && targetDataWithEmploymentCost?.amounts.costOfLabor ? (
                              <>
                                <span className="text-4xl font-semibold text-primary-600 ">
                                  <AnimatedNumber
                                    duration={150}
                                    formatValue={(value) => {
                                      return money.format(value, { roundTo: 100 });
                                    }}
                                    value={targetDataWithEmploymentCost.amounts.costOfLabor}
                                  />
                                </span>
                                <span className="ml-auto text-xl font-semibold text-primary-600 lg:ml-2">
                                  <span className="font-light text-primary-200">(</span>
                                  <PercentageDifference
                                    compare={targetDataWithEmploymentCost.amounts.costOfLabor}
                                    to={convertedSourceAmountWithEmploymentCost * 100}
                                  />
                                  <span className="font-light text-primary-200">)</span>
                                </span>
                              </>
                            ) : (
                              <span className="text-5xl font-semibold text-primary-600">-</span>
                            )}
                          </div>
                        </div>

                        {/* Cost of living */}
                        <div>
                          <span className="flex items-center space-x-1 font-medium uppercase text-gray-500">
                            <span className="whitespace-nowrap">Based on cost of living</span>

                            <Tooltip
                              title={
                                <Typography variant="inherit">
                                  <span className="font-semibold">“Cost of living”</span> refers to the costs to a
                                  consumer in a specific geographic area. It reflects the price of food, housing,
                                  groceries, transportation, taxes and entertainment. Simply put, it’s the cost of
                                  maintaining a certain standard of living.
                                </Typography>
                              }
                            >
                              <InfoOutlined fontSize="small" className="ml-2 text-gray-400" />
                            </Tooltip>
                          </span>

                          <div className="mt-3 flex items-end">
                            {data && targetDataWithEmploymentCost?.amounts.costOfLiving ? (
                              <>
                                <span className="text-4xl font-semibold text-primary-600 ">
                                  <AnimatedNumber
                                    duration={150}
                                    formatValue={(value) => {
                                      return money.format(value, { roundTo: 100 });
                                    }}
                                    value={targetDataWithEmploymentCost.amounts.costOfLiving}
                                  />
                                </span>
                                <span className="ml-auto text-xl font-semibold text-primary-600 lg:ml-2">
                                  <span className="font-light text-primary-200">(</span>
                                  <PercentageDifference
                                    compare={targetDataWithEmploymentCost.amounts.costOfLiving}
                                    to={convertedSourceAmountWithEmploymentCost * 100}
                                  />
                                  <span className="font-light text-primary-200">)</span>
                                </span>
                              </>
                            ) : (
                              <span className="text-5xl font-semibold text-primary-600">-</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Disclosure.Panel>
              </Disclosure>
            )}
          </Card>
        </div>

        <div className="mt-4 flex justify-between px-2">
          {data?.local.currency.id !== data?.converted.currency.id && (
            <Button
              color="inherit"
              size="small"
              variant="text"
              startIcon={<RefreshRounded />}
              onClick={() => setUseConvertedCurrency(!useConvertedCurrency)}
            >
              {useConvertedCurrency && <span>Display in {data?.local.currency.name}</span>}
              {!useConvertedCurrency && <span>Convert to {data?.converted.currency.name}</span>}
            </Button>
          )}
        </div>
      </form>

      <Card className="mt-8 flex animate-scale flex-col !p-4 sm:mt-16">
        <LegacyHeading className="font-title" size="2xl">
          Our methodology
        </LegacyHeading>

        {landingStats && (
          <LegacyText className="mt-4 border-t pt-4 ">
            This converter is based on a combination of multiple sources, the main one being Figures' own data, coming
            from our {landingStats.companiesCount} clients & public salary / cost of living data gathered from public
            sources. We're continuously working on adding more sources to increase quality.
          </LegacyText>
        )}

        <div className="mt-6 grid grid-cols-1 gap-y-8 gap-x-16 lg:grid-cols-4">
          <div>
            <span className="flex items-center space-x-1 text-xs font-medium uppercase text-gray-500">
              <Icon name="database" />
              <span className="whitespace-nowrap">Data sources</span>
            </span>

            <div className="mt-3 flex items-end">
              {data?.sourcesCount ? (
                <span className="text-4xl font-semibold text-primary-600 ">
                  <AnimatedNumber
                    duration={150}
                    formatValue={(amount) => {
                      return `${Math.round(amount)}`;
                    }}
                    value={data.sourcesCount}
                  />
                  <Link newTab noLinkStyle className="text-2xl" to="/home">
                    , incl. Figures
                  </Link>
                </span>
              ) : (
                <span className="text-5xl font-semibold text-primary-600">-</span>
              )}
            </div>
          </div>

          <div>
            <span className="flex items-center space-x-1 text-xs font-medium uppercase text-gray-500">
              <Icon name="group" />
              <span className="whitespace-nowrap">Data points used to calculate results</span>
            </span>

            <div className="mt-3 flex items-end">
              {data?.employeesCount ? (
                <span className="text-4xl font-semibold text-primary-600 ">
                  <AnimatedNumber
                    duration={150}
                    formatValue={(amount) => {
                      return `${Math.round(amount)}`;
                    }}
                    value={data.employeesCount}
                  />
                </span>
              ) : (
                <span className="text-xl font-semibold text-secondary-600">Low data points count</span>
              )}
            </div>
          </div>
        </div>

        {showMoreInformationSection && (
          <Card className="mt-8 flex animate-scale flex-col !p-4">
            <LegacyHeading className="font-title" size="2xl">
              More about {form.values.toLocation?.name}
            </LegacyHeading>

            <div className="mt-4 grid grid-cols-1 gap-y-8 gap-x-16 border-t pt-4 lg:grid-cols-4">
              {data?.destination.oysterUrl && (
                <div>
                  <span className="flex items-center space-x-1 text-xs font-medium uppercase text-gray-500">
                    <Icon name="document" className="flex-shrink-0" />
                    <span className="whitespace-nowrap">Hiring in {form.values.toLocation?.name}</span>
                  </span>

                  <div className="mt-3 flex items-end">
                    <span className="text-xl font-semibold text-gray-700 ">
                      <Link newTab className="flex items-center" to={data.destination.oysterUrl as ExternalLink}>
                        <span>oysterhr.com</span>
                        <Icon className="ml-1" name="export" />
                      </Link>
                    </span>
                  </div>
                </div>
              )}

              {data?.destination.nomadListUrl && (
                <div>
                  <span className="flex items-center space-x-1 text-xs font-medium uppercase text-gray-500">
                    <Icon name="hail" className="flex-shrink-0" />
                    <span className="whitespace-nowrap">Working remotely in {form.values.toLocation?.name}</span>
                  </span>

                  <div className="mt-3 flex items-end">
                    <span className="text-xl font-semibold text-gray-700 ">
                      <Link newTab className="flex items-center" to={data.destination.nomadListUrl as ExternalLink}>
                        <span>nomadlist.com</span>
                        <Icon className="ml-1" name="export" />
                      </Link>
                    </span>
                  </div>
                </div>
              )}

              {data?.destination.youTubeSongUrl && (
                <div>
                  <span className="flex items-center space-x-1 text-xs font-medium uppercase text-gray-500">
                    <Icon name="youtube" className="flex-shrink-0" />
                    <span className="whitespace-nowrap">{form.values.toLocation?.name} in Music</span>
                  </span>

                  <div className="mt-3 flex items-end">
                    <span className="text-xl font-semibold text-gray-700 ">
                      <Link newTab className="flex items-center" to={data.destination.youTubeSongUrl as ExternalLink}>
                        <span>youtube.com</span>
                        <Icon className="ml-1" name="export" />
                      </Link>
                    </span>
                  </div>
                </div>
              )}

              <div>
                <span className="flex items-center space-x-1 text-xs font-medium uppercase text-gray-500">
                  <Icon name="check" className="flex-shrink-0" />
                  <span className="whitespace-nowrap">Get to know your next hire</span>
                </span>

                <div className="mt-3 flex items-end">
                  <span className="text-xl font-semibold text-gray-700 ">
                    <Link newTab className="flex items-center" to="https://www.veremark.com/">
                      <span>veremark.com</span>
                      <Icon className="ml-1" name="export" />
                    </Link>
                  </span>
                </div>
              </div>
            </div>
          </Card>
        )}
      </Card>
    </div>
  );
};
